import { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import { shortLabel } from "./FrsTab";

/** Unmatched-to-estimate report, both directions, with manual link / dismiss. */
export default function FrsUnmatched({ project, canEdit, onChanged, tick }) {
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => opsApi.frsUnmatched(project.id).then(setRep).catch(() => toast.error("Could not load the report"));
  useEffect(() => { setRep(null); load(); }, [project.id, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const link = async (itemId, lineId, dismiss = false) => {
    try { await opsApi.frsLink(project.id, { item_id: itemId, estimate_line_id: lineId || "", dismiss }); toast.success(dismiss ? "Marked field-only" : "Linked"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not link"); }
  };
  const rematch = async () => {
    setBusy(true);
    try { const r = await opsApi.frsRematch(project.id); toast.success(`${r.linked} of ${r.items} items linked`); onChanged(); }
    catch (e) { toast.error("Re-match failed"); } finally { setBusy(false); }
  };

  if (!rep) return <div className="flex items-center gap-2 py-10 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const active = rep.unmatched_items.filter((i) => !i.estimate_link_dismissed);
  const dismissed = rep.unmatched_items.filter((i) => i.estimate_link_dismissed);

  return (
    <div className="space-y-4" data-testid="ops-frs-unmatched">
      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
        {rep.estimate_id ? <span>Linked against the working estimate. <span className="font-medium text-slate-800" data-testid="ops-frs-unmatched-count">{active.length}</span> FRS items without a line · <span className="font-medium text-slate-800">{rep.unmatched_lines.length}</span> estimate lines without an item{dismissed.length ? ` · ${dismissed.length} marked field-only` : ""}.</span>
          : <span>No working estimate on this job yet.</span>}
        {canEdit && rep.estimate_id && <Button size="sm" variant="outline" onClick={rematch} disabled={busy} className="ml-auto" data-testid="ops-frs-rematch"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Re-match</Button>}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">FRS items without an estimate line ({active.length})</div>
          {active.map((it) => (
            <div key={it.id} className="flex flex-wrap items-center gap-2 border-b border-slate-50 px-3 py-2 text-sm" data-testid={`ops-frs-unmatched-item-${it.id}`}>
              <span className="w-28 text-xs text-slate-500">{[it.building, it.room_number].filter(Boolean).join(" / ")}</span>
              <span className="w-12 text-xs text-slate-500">{it.scope_code}</span>
              <span className="text-slate-800">{shortLabel(it)}</span>
              <span className="tabular-nums text-slate-600">× {it.effective_quantity}</span>
              {canEdit && (
                <span className="ml-auto flex items-center gap-1">
                  <select defaultValue="" onChange={(e) => e.target.value && link(it.id, e.target.value)} className="h-7 max-w-[220px] rounded-md border border-slate-200 bg-white px-1 text-xs" data-testid={`ops-frs-link-select-${it.id}`}>
                    <option value="">Link to line…</option>
                    {rep.lines.map((l) => <option key={l.id} value={l.id}>{l.section} · {l.description}{l.item_number ? ` (${l.item_number})` : ""}</option>)}
                  </select>
                  <button onClick={() => link(it.id, "", true)} className="text-xs text-slate-500 hover:text-slate-800" data-testid={`ops-frs-dismiss-${it.id}`}>field-only</button>
                </span>
              )}
            </div>
          ))}
          {!active.length && <div className="px-3 py-6 text-center text-sm text-slate-500">Every FRS item is linked.</div>}
        </div>
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Estimate lines without an FRS item ({rep.unmatched_lines.length})</div>
          {rep.unmatched_lines.map((l) => (
            <div key={l.id} className="flex items-center gap-2 border-b border-slate-50 px-3 py-2 text-sm" data-testid={`ops-frs-unmatched-line-${l.id}`}>
              <span className="w-32 truncate text-xs text-slate-500">{l.section}</span>
              <span className="truncate text-slate-800">{l.description}</span>
              {l.manufacturer && <span className="text-xs text-slate-400">{l.manufacturer}</span>}
              <span className="ml-auto tabular-nums text-slate-600">{l.qty} {l.uom || ""}</span>
            </div>
          ))}
          {!rep.unmatched_lines.length && <div className="px-3 py-6 text-center text-sm text-slate-500">{rep.estimate_id ? "Every estimate line has an FRS item." : "—"}</div>}
        </div>
      </div>
    </div>
  );
}
